import { displayJourneyUsingMetatags } from '@eventbrite/branchio-utils';
import { sdkRequest } from '@eventbrite/http';
import { setLanguage } from '@eventbrite/i18n';
import {
    addDimensions,
    trackAnalyticsEvent,
    trackInitialPageView,
} from '@eventbrite/site-analytics';
import { logEvent } from '@eventbrite/statsig';
import React, { useEffect, useMemo, useState } from 'react';
import BookmarkRoot from './components/Bookmark/BookmarkRoot';
import CreatorCollectionsRoot from './components/CreatorCollections/CreatorCollectionsRoot';
import { EnhancedListingComponents } from './components/EnhancedListingComponents/EnhancedListingComponents';
import { ExpiredEvents } from './components/ExpiredEvents/ExpiredEvents';
import { NotificationBar } from './components/NotificationBar/NotificationBar';
import { ReportThisEventRoot } from './components/ReportThisEvent/ReportThisEventRoot';
import SeriesDatesDropdownRoot from './components/SeriesDatesDropdown/SeriesDatesDropdownRoot';
import ShareEventRoot from './components/ShareEvent/ShareEventRoot';
import { Analytics } from './components/utils/analytics';
import { STATSIG_EVENT_NAMES } from './constants';
import {
    OrganizerContext,
    SettingsContext,
    TrackingContext,
    UserContext,
} from './context';
import { NotificationsContextProvider } from './contexts';
import { getTrackingContextFrom } from './getTrackingContext';
import { dataLayerMiddleware } from './tracking/dataLayerMiddleware';
import type {
    AppProps,
    CollectionResponse,
    EventCollectionsResponse,
} from './types';
import { GACodes } from './types';

const setUpDimensions = (props: AppProps): void => {
    const {
        app_name: appName,
        app_version: appVersion,
        event,
        gaSettings,
    } = props;
    const {
        activeScopeUserId,
        correlationId,
        guestPartnerId,
        userPartnerId: userId,
        guestPartnerId: guestId,
    } = gaSettings;
    const {
        id: eventId,
        ownerId: eventOwnerUserId,
        source: eventSource,
    } = event;

    const dimensions = {
        activeScopeUserId,
        appName,
        appVersion,
        correlationId,
        eventId,
        eventOwnerUserId,
        eventSource,
        experimentId: 'OldListing',
        guestId,
        guestPartnerId,
        userId,
    };

    addDimensions(dimensions);
};

const useCreatorCollections = (
    eventId: string,
    launchLinkToCreatorCollectionsOnListing: boolean,
    isExpiredEvent: boolean,
) => {
    const [collections, setCollections] = useState<
        CollectionResponse[] | undefined
    >(undefined);

    useEffect(() => {
        let isMounted = true;

        displayJourneyUsingMetatags({
            name: 'DisplayBranchBanner',
            content: '1',
            extraData: {
                customActionParams: {
                    EventId: eventId,
                },
            },
        });

        if (isExpiredEvent) {
            return;
        }

        if (launchLinkToCreatorCollectionsOnListing) {
            sdkRequest(`/events/${eventId}/collections/public/`).then(
                (storedCollections: EventCollectionsResponse) => {
                    if (storedCollections && isMounted)
                        return setCollections(storedCollections.collections);
                },
            );
        } else {
            return setCollections([]);
        }
        return () => {
            isMounted = false;
        };
    }, [eventId, launchLinkToCreatorCollectionsOnListing, isExpiredEvent]);

    return collections;
};

export const App: React.FC<AppProps> = (props: AppProps) => {
    const {
        env: {
            localeInfo: { locale },
        },
        event: {
            childEvents,
            id: eventId,
            isOnlineEvent,
            isExpiredEvent = false,
        },
        settings: {
            featureFlags: {
                launchLinkToCreatorCollectionsOnListing = false,
            } = {},
        },
        tracking,
        components: { bookmark: { isBookmarkedEvent = false } = {} },
    } = props;

    setLanguage(locale);

    setUpDimensions(props);

    const modalContainer = React.useRef<HTMLDivElement | null>(null);
    const creatorCollections = useCreatorCollections(
        eventId,
        launchLinkToCreatorCollectionsOnListing,
        isExpiredEvent,
    );

    if (isExpiredEvent) {
        ExpiredEvents();
    }

    Analytics({ ...tracking, eventId });

    useEffect(() => {
        trackInitialPageView(props.gaSettings, {
            dimensions: { eventId },
        });

        trackAnalyticsEvent({
            category: GACodes.GA_CATEGORY_LISTING,
            action: GACodes.GA_ACTION_APP_LOAD,
            label: props.user.canManageEvent
                ? GACodes.GA_LABEL_CAN_EDIT
                : GACodes.GA_LABEL_CANNOT_EDIT,
        });
        logEvent(STATSIG_EVENT_NAMES.LISTING_APP_PAGE_VIEW);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useMemo(() => {
        // This method requires the use of `useMemo` otherwise tracking
        // in nested components may be called out of order and not receive
        // the page props intended to be set by the middleware.
        dataLayerMiddleware(
            props.event,
            props.components,
            props.urgencySignals,
        );
    }, [props.event, props.components, props.urgencySignals]);

    return (
        <div data-spec="listing-page-app">
            <SettingsContext.Provider value={props.settings}>
                <UserContext.Provider value={props.user}>
                    <TrackingContext.Provider
                        value={getTrackingContextFrom(props)}
                    >
                        <OrganizerContext.Provider value={props.organizer}>
                            <ShareEventRoot
                                {...props}
                                modalContainer={modalContainer}
                            />
                            <NotificationsContextProvider>
                                <NotificationBar />
                                <BookmarkRoot
                                    eventId={eventId}
                                    isBookmarked={isBookmarkedEvent}
                                    queryParams={location.search}
                                />
                            </NotificationsContextProvider>
                            <SeriesDatesDropdownRoot
                                childEvents={childEvents || []}
                                eventId={eventId}
                                isOnlineEvent={isOnlineEvent}
                            />
                            <ReportThisEventRoot {...props} />
                            <CreatorCollectionsRoot {...props} />
                            <EnhancedListingComponents
                                {...props}
                                creatorCollections={creatorCollections}
                            />
                            <div
                                id="share-modal-wrapper"
                                ref={modalContainer}
                            ></div>
                            <div id="listings-root__overlay"></div>
                        </OrganizerContext.Provider>
                    </TrackingContext.Provider>
                </UserContext.Provider>
            </SettingsContext.Provider>
        </div>
    );
};
